import { useCallback } from 'react';

import {
	createActionsHook,
	createHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type Store as RSSStore,
	type StoreActionApi,
} from 'react-sweet-state';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import type {
	ChatContextPayload,
	ChatContextState,
} from '@atlaskit/rovo-triggers/chat-context/types';
import type {
	BrowserContextPayloadData,
	DashboardInsightsActionsPayloadData,
	DatabaseContextPayloadData,
	EditorContextPayloadData,
	JiraCreateContextPayloadData,
	SlidesContextPayloadData,
	SmartLinksContextPayload,
	WhiteboardContextPayloadData,
	WorkflowContextPayloadData,
} from '@atlaskit/rovo-triggers/types';
import type { BrowserContextCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/browser-context-command';
import type {
	JiraContextCommandResult,
	JiraContextData,
} from '@atlassian/rovo-content-bridge-api-commands/commands/jira-draft-data-command';
import type { ReadContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import type { ReadSpaceCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/read-space-command';

/** Single 3P connector entry published by the host (e.g. Confluence view page). */
export type PageSmartLink = NonNullable<SmartLinksContextPayload['data']>[number];

export type Store = {
	editorContext: EditorContextPayloadData;
	whiteboardContext?: WhiteboardContextPayloadData;
	slidesContext?: SlidesContextPayloadData;
	databaseContext?: DatabaseContextPayloadData;
	browserContext?: BrowserContextPayloadData;
	dashboardContext?: DashboardInsightsActionsPayloadData;
	jiraWorkflowContext?: WorkflowContextPayloadData;
	jiraCreateContext?: JiraCreateContextPayloadData;
	// All the new context will be added under this key
	aiFeatureContext: ChatContextState;
	/**
	 * Generic "user dismissed the chat context" flag, flipped to `true` when the
	 * user clicks the "X" / Remove context button in the chat banner. While this
	 * flag is `true`, `setAIFeatureContext` becomes a no-op so upstream publishers
	 * (e.g. the Jira list/backlog view context publishers that re-publish on every
	 * selection change) cannot silently re-attach the dismissed context to outgoing
	 * messages. Reset on page reload, or explicitly via `setAIFeatureContextDismissed(false)`.
	 */
	isAIFeatureContextDismissed: boolean;
	/**
	 * For AI-first creation, we need to store the content targets in the store.
	 * This is used to determine which content target to use for content mutations.
	 * Supports arrays of targets per context.
	 * Otherwise, it stores single targets for backward compatibility.
	 */
	contentTargets: Partial<Record<ContentContext, ContentTarget | ContentTarget[]>>;
	/**
	 * The id of the current content target, which will be used to determine which context
	 * to use for create and edit mutations.
	 */
	currentTargetId: string | number | undefined;
	/**
	 * Smart links detected on the current page, grouped by connector source with occurrence counts.
	 * Used to drive the proactive 3P connector recommendation banner.
	 * Populated via 'smartlinks-context-payload' from the host (e.g. Confluence view page).
	 */
	pageSmartLinks?: PageSmartLink[];
};

/**
 * Content types supported in the AI-first creation flow.
 */
export type ContentType = 'page' | 'whiteboard' | 'slide' | 'database' | 'dashboard';

/**
 * A piece of content which Rovo chat is able to communicate with directly in order
 * to mutate it after it has been created. Considerations:
 *
 * - Rovo <-> content can communicate via inline experience inside of the content (e.g. the inline toolbar)
 * - Rovo <-> content[] is a possibility - consider 'layering' of the staging area in AI-first creation.
 * - Rovo <-> content[] is a future possibility with more and more content mutations being possible via Rovo.
 */
type ContentTarget = {
	/**
	 * The id of the content target, which will be supplied to backend APIs,
	 * and yielded in every chunk of network stream events.
	 */
	id: string | number;
	/**
	 * The type of the content target, which will be used to determine which context
	 * to use for create and edit mutations.
	 */
	type?: 'page' | 'whiteboard' | 'slide' | 'space' | 'jira' | 'database' | 'dashboard';
	/**
	 * Whether the content target can provide context for create and edit mutations.
	 */
	canProvideContext: boolean;
};

/**
 * Content is rendered in a Rovo context. If it is 'real' content outside of the staging area, it is rendered
 * in the `global` Rovo context (with Rovo side-chat being a global experience). If it is rendered in the staging
 * area modal, then it is rendered to the left, with chat embedded in the same UI experience.
 */
export type ContentContext = 'staging-area' | 'global' | 'blank-object-experience';

const store: RSSStore<
	Store,
	{
		getAllContext: () => ({ getState }: StoreActionApi<Store>) => Readonly<Store>;
		getAIFeatureContext: () => ({
			getState,
		}: StoreActionApi<Store>) => ChatContextState | undefined;
		setAIFeatureContext: (
			chatContextData: ChatContextPayload,
		) => ({ getState, setState }: StoreActionApi<Store>) => void;
		setAIFeatureContextDismissed: (
			isDismissed: boolean,
		) => ({ setState }: StoreActionApi<Store>) => void;
		getDashboardContext: () => ({
			getState,
		}: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData;
		setDashboardContext: (
			dashboardContext: DashboardInsightsActionsPayloadData,
		) => ({ setState }: StoreActionApi<Store>) => void;
		getJiraWorkflowContext: () => ({
			getState,
		}: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined;
		setJiraWorkflowContext: (
			jiraWorkflowContext: WorkflowContextPayloadData,
		) => ({ setState }: StoreActionApi<Store>) => void;
		getJiraCreateContext: () => ({
			getState,
		}: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined;
		setJiraCreateContext: (
			jiraCreateContext: JiraCreateContextPayloadData,
		) => ({ setState }: StoreActionApi<Store>) => void;
		getEditorContext: () => ({ getState }: StoreActionApi<Store>) => EditorContextPayloadData;
		setEditorContext: (
			editorContext: EditorContextPayloadData,
		) => ({ setState }: StoreActionApi<Store>) => void;
		getWhiteboardContext: () => ({
			getState,
		}: StoreActionApi<Store>) => WhiteboardContextPayloadData;
		setWhiteboardContext: (
			whiteboardContext: WhiteboardContextPayloadData,
		) => ({ setState }: StoreActionApi<Store>) => void;
		getSlidesContext: () => ({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData;
		setSlidesContext: (
			slidesContext: SlidesContextPayloadData,
		) => ({ setState }: StoreActionApi<Store>) => void;
		getDatabaseContext: () => ({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData;
		setDatabaseContext: (
			databaseContext: DatabaseContextPayloadData,
		) => ({ setState }: StoreActionApi<Store>) => void;
		mergeDatabaseContext: (
			partial: Partial<NonNullable<DatabaseContextPayloadData>>,
		) => ({ getState, setState }: StoreActionApi<Store>) => void;
		getBrowserContext: () => ({
			getState,
		}: StoreActionApi<Store>) => BrowserContextPayloadData | undefined;
		setBrowserContext: (
			browserContext: BrowserContextPayloadData,
		) => ({ setState }: StoreActionApi<Store>) => void;
		setPageSmartLinks: (
			pageSmartLinks: PageSmartLink[] | undefined,
		) => ({ setState }: StoreActionApi<Store>) => void;
		getTarget: (
			contentContext: ContentContext,
			activeTargetId?: string | number,
		) => ({ getState }: StoreActionApi<Store>) => ContentTarget | undefined;
		getContentContext: (
			contentId: string | number,
		) => ({ getState }: StoreActionApi<Store>) => ContentContext | undefined;
		getActiveContext: () => ({ getState }: StoreActionApi<Store>) => ContentContext;
		hasTarget: (contentContext: ContentContext) => ({ getState }: StoreActionApi<Store>) => boolean;
		setTarget: (
			toAdd: ContentContext,
			target: ContentTarget,
		) => ({ getState, setState }: StoreActionApi<Store>) => void;
		clearTarget: (
			toRemove: ContentContext,
			targetId?: string | number,
		) => ({ getState, setState }: StoreActionApi<Store>) => void;
		setCurrentTargetId: (
			targetId: string | number | undefined,
		) => ({ setState }: StoreActionApi<Store>) => void;
		getCurrentTargetId: () => ({ getState }: StoreActionApi<Store>) => string | number | undefined;
	}
> = createStore({
	initialState: {
		editorContext: undefined,
		whiteboardContext: undefined,
		slidesContext: undefined,
		databaseContext: undefined,
		browserContext: undefined,
		dashboardContext: undefined,
		jiraWorkflowContext: undefined,
		aiFeatureContext: {},
		isAIFeatureContextDismissed: false,
		contentTargets: {},
		currentTargetId: undefined,
		pageSmartLinks: undefined,
	} as Store,
	actions: {
		getAllContext:
			(): (({ getState }: StoreActionApi<Store>) => Readonly<Store>) =>
			({ getState }) =>
				getState(),
		getAIFeatureContext:
			(): (({ getState }: StoreActionApi<Store>) => ChatContextState | undefined) =>
			({ getState }) => {
				const currentState = getState().aiFeatureContext;
				// Delete all properties that are undefined
				Object.keys(currentState).forEach(
					(key) => currentState[key] === undefined && delete currentState[key],
				);
				// Avoid sending an empty object
				return Object.keys(currentState).length ? currentState : undefined;
			},
		setAIFeatureContext:
			(
				chatContextData: ChatContextPayload,
			): (({ getState, setState }: StoreActionApi<Store>) => void) =>
			({ getState, setState }) => {
				const state = getState();
				// Honour the user's explicit "Remove context" action
				if (state.isAIFeatureContextDismissed) {
					return;
				}
				const currentAIFeatureContextState = state.aiFeatureContext;
				const { contextKey, setContext } = chatContextData;
				if (contextKey) {
					const contextUpdated = setContext(currentAIFeatureContextState[contextKey]);

					setState({
						aiFeatureContext: {
							...currentAIFeatureContextState,
							[contextKey]: contextUpdated,
						},
					});
				}
			},
		setAIFeatureContextDismissed:
			(isDismissed: boolean): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState(
					isDismissed
						? { aiFeatureContext: {}, isAIFeatureContextDismissed: true }
						: { isAIFeatureContextDismissed: false },
				);
			},
		getDashboardContext:
			(): (({ getState }: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData) =>
			({ getState }) =>
				getState().dashboardContext,
		setDashboardContext:
			(
				dashboardContext: DashboardInsightsActionsPayloadData,
			): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState({
					dashboardContext,
				});
			},
		getJiraWorkflowContext:
			(): (({ getState }: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined) =>
			({ getState }) =>
				getState().jiraWorkflowContext,
		setJiraWorkflowContext:
			(
				jiraWorkflowContext: WorkflowContextPayloadData,
			): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState({
					jiraWorkflowContext,
				});
			},
		getJiraCreateContext:
			(): (({ getState }: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined) =>
			({ getState }) =>
				getState().jiraCreateContext,
		setJiraCreateContext:
			(
				jiraCreateContext: JiraCreateContextPayloadData,
			): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState({
					jiraCreateContext,
				});
			},
		getEditorContext:
			(): (({ getState }: StoreActionApi<Store>) => EditorContextPayloadData) =>
			({ getState }) =>
				getState().editorContext,
		setEditorContext:
			(editorContext: EditorContextPayloadData): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState({
					editorContext,
				});
			},
		getWhiteboardContext:
			(): (({ getState }: StoreActionApi<Store>) => WhiteboardContextPayloadData) =>
			({ getState }) =>
				getState().whiteboardContext,
		setWhiteboardContext:
			(
				whiteboardContext: WhiteboardContextPayloadData,
			): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState({
					whiteboardContext,
				});
			},
		getSlidesContext:
			(): (({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData) =>
			({ getState }) =>
				getState().slidesContext,
		setSlidesContext:
			(slidesContext: SlidesContextPayloadData): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState({
					slidesContext,
				});
			},
		getDatabaseContext:
			(): (({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData) =>
			({ getState }) =>
				getState().databaseContext,
		setDatabaseContext:
			(
				databaseContext: DatabaseContextPayloadData,
			): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState({
					databaseContext,
				});
			},
		mergeDatabaseContext:
			(
				partial: Partial<NonNullable<DatabaseContextPayloadData>>,
			): (({ getState, setState }: StoreActionApi<Store>) => void) =>
			({ getState, setState }) => {
				// If no selected elements published, clean up the database context
				if (!partial?.selectedElementIds) {
					setState({
						databaseContext: undefined,
					});
					return;
				}

				const current = getState().databaseContext;
				setState({
					databaseContext: current
						? { ...current, ...partial }
						: (partial as DatabaseContextPayloadData),
				});
			},
		getBrowserContext:
			(): (({ getState }: StoreActionApi<Store>) => BrowserContextPayloadData | undefined) =>
			({ getState }) =>
				getState().browserContext,
		setBrowserContext:
			(
				browserContext: BrowserContextPayloadData,
			): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState({
					browserContext,
				});
			},
		setPageSmartLinks:
			(
				pageSmartLinks: PageSmartLink[] | undefined,
			): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }) => {
				setState({
					pageSmartLinks,
				});
			},
		getTarget:
			(
				contentContext: ContentContext,
				activeTargetId?: string | number,
			): (({ getState }: StoreActionApi<Store>) => ContentTarget | undefined) =>
			({ getState }) => {
				const targets = getState().contentTargets[contentContext];

				if (!targets) {
					return undefined;
				}

				// targets is always an array
				const targetList = targets as ContentTarget[];

				// If activeTargetId provided, find it; otherwise return first target
				if (activeTargetId) {
					return targetList.find((t) => t.id === activeTargetId);
				}
				return targetList[0];
			},
		getContentContext:
			(
				contentId: string | number,
			): (({ getState }: StoreActionApi<Store>) => ContentContext | undefined) =>
			({ getState }) => {
				const entries = Object.entries(getState().contentTargets);
				for (const [context, targets] of entries) {
					const targetList = targets as ContentTarget[];

					if (targetList.some((t) => t.id === contentId)) {
						return context as ContentContext;
					}
				}
				return undefined;
			},
		getActiveContext:
			(): (({ getState }: StoreActionApi<Store>) => ContentContext) =>
			({ getState }): ContentContext => {
				const entries = getState().contentTargets;
				// The rules here determine the 'rank' in which Rovo layers are resolved into "which layer is currently active".
				// They map to intended UX - in AIFC, the staging area is a modal-powered experience, on top of global experiences.
				// Consider the scenario of sending a message - the user will be interacting with the topmost layer.
				if (entries['staging-area']) {
					return 'staging-area';
				}
				return 'global';
			},
		hasTarget:
			(contentContext: ContentContext): (({ getState }: StoreActionApi<Store>) => boolean) =>
			({ getState }) =>
				!!getState().contentTargets[contentContext],
		setTarget:
			(
				toAdd: ContentContext,
				target: ContentTarget,
			): (({ getState, setState }: StoreActionApi<Store>) => void) =>
			({ getState, setState }) => {
				const currentTargets = getState().contentTargets;

				// Store as array, append if not already present
				const existing = currentTargets[toAdd] as ContentTarget[] | undefined;
				const targetList = existing ?? [];

				// Avoid duplicates by checking if target with same id exists
				// & filter out any targets with empty IDs to avoid stale targets
				const filtered = targetList.filter((t) => t.id !== '' && t.id !== target.id);
				filtered.push(target);

				setState({
					contentTargets: { ...currentTargets, [toAdd]: filtered },
				});
			},
		clearTarget:
			(
				toRemove: ContentContext,
				targetId?: string | number,
			): (({ getState, setState }: StoreActionApi<Store>) => void) =>
			({ getState, setState }) => {
				const currentState = getState().contentTargets;
				const nextState: Partial<Record<ContentContext, ContentTarget | ContentTarget[]>> = {};

				for (const key of Object.keys(currentState)) {
					const k = key as ContentContext;
					if (k !== toRemove) {
						nextState[k] = currentState[k];
					} else if (targetId) {
						// If a specific targetId is provided, filter out that target
						const targetList = currentState[k] as ContentTarget[];
						const filtered = targetList.filter((t) => t.id !== targetId);
						if (filtered.length > 0) {
							nextState[k] = filtered;
						}
					}
				}

				setState({ contentTargets: nextState });
			},
		setCurrentTargetId:
			(targetId: string | number | undefined): (({ setState }: StoreActionApi<Store>) => void) =>
			({ setState }): void => {
				setState({
					currentTargetId: targetId,
				});
			},
		getCurrentTargetId:
			(): (({ getState }: StoreActionApi<Store>) => string | number | undefined) =>
			({ getState }): string | number | undefined =>
				getState().currentTargetId,
	},
});

export const useChatContextStoreActions: HookActionsFunction<
	BoundActions<
		Store,
		{
			getAllContext: () => ({ getState }: StoreActionApi<Store>) => Readonly<Store>;
			getAIFeatureContext: () => ({
				getState,
			}: StoreActionApi<Store>) => ChatContextState | undefined;
			setAIFeatureContext: (
				chatContextData: ChatContextPayload,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setAIFeatureContextDismissed: (
				isDismissed: boolean,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDashboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData;
			setDashboardContext: (
				dashboardContext: DashboardInsightsActionsPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraWorkflowContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined;
			setJiraWorkflowContext: (
				jiraWorkflowContext: WorkflowContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraCreateContext: () => ({
				getState,
			}: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined;
			setJiraCreateContext: (
				jiraCreateContext: JiraCreateContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getEditorContext: () => ({ getState }: StoreActionApi<Store>) => EditorContextPayloadData;
			setEditorContext: (
				editorContext: EditorContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getWhiteboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WhiteboardContextPayloadData;
			setWhiteboardContext: (
				whiteboardContext: WhiteboardContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getSlidesContext: () => ({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData;
			setSlidesContext: (
				slidesContext: SlidesContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDatabaseContext: () => ({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData;
			setDatabaseContext: (
				databaseContext: DatabaseContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			mergeDatabaseContext: (
				partial: Partial<NonNullable<DatabaseContextPayloadData>>,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			getBrowserContext: () => ({
				getState,
			}: StoreActionApi<Store>) => BrowserContextPayloadData | undefined;
			setBrowserContext: (
				browserContext: BrowserContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			setPageSmartLinks: (
				pageSmartLinks: PageSmartLink[] | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getTarget: (
				contentContext: ContentContext,
				activeTargetId?: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentTarget | undefined;
			getContentContext: (
				contentId: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentContext | undefined;
			getActiveContext: () => ({ getState }: StoreActionApi<Store>) => ContentContext;
			hasTarget: (
				contentContext: ContentContext,
			) => ({ getState }: StoreActionApi<Store>) => boolean;
			setTarget: (
				toAdd: ContentContext,
				target: ContentTarget,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			clearTarget: (
				toRemove: ContentContext,
				targetId?: string | number,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setCurrentTargetId: (
				targetId: string | number | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getCurrentTargetId: () => ({
				getState,
			}: StoreActionApi<Store>) => string | number | undefined;
		}
	>
> = createActionsHook(store);
export const useChatContextEditorContext: HookFunction<
	EditorContextPayloadData,
	BoundActions<
		Store,
		{
			getAllContext: () => ({ getState }: StoreActionApi<Store>) => Readonly<Store>;
			getAIFeatureContext: () => ({
				getState,
			}: StoreActionApi<Store>) => ChatContextState | undefined;
			setAIFeatureContext: (
				chatContextData: ChatContextPayload,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setAIFeatureContextDismissed: (
				isDismissed: boolean,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDashboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData;
			setDashboardContext: (
				dashboardContext: DashboardInsightsActionsPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraWorkflowContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined;
			setJiraWorkflowContext: (
				jiraWorkflowContext: WorkflowContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraCreateContext: () => ({
				getState,
			}: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined;
			setJiraCreateContext: (
				jiraCreateContext: JiraCreateContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getEditorContext: () => ({ getState }: StoreActionApi<Store>) => EditorContextPayloadData;
			setEditorContext: (
				editorContext: EditorContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getWhiteboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WhiteboardContextPayloadData;
			setWhiteboardContext: (
				whiteboardContext: WhiteboardContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getSlidesContext: () => ({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData;
			setSlidesContext: (
				slidesContext: SlidesContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDatabaseContext: () => ({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData;
			setDatabaseContext: (
				databaseContext: DatabaseContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			mergeDatabaseContext: (
				partial: Partial<NonNullable<DatabaseContextPayloadData>>,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			getBrowserContext: () => ({
				getState,
			}: StoreActionApi<Store>) => BrowserContextPayloadData | undefined;
			setBrowserContext: (
				browserContext: BrowserContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			setPageSmartLinks: (
				pageSmartLinks: PageSmartLink[] | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getTarget: (
				contentContext: ContentContext,
				activeTargetId?: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentTarget | undefined;
			getContentContext: (
				contentId: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentContext | undefined;
			getActiveContext: () => ({ getState }: StoreActionApi<Store>) => ContentContext;
			hasTarget: (
				contentContext: ContentContext,
			) => ({ getState }: StoreActionApi<Store>) => boolean;
			setTarget: (
				toAdd: ContentContext,
				target: ContentTarget,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			clearTarget: (
				toRemove: ContentContext,
				targetId?: string | number,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setCurrentTargetId: (
				targetId: string | number | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getCurrentTargetId: () => ({
				getState,
			}: StoreActionApi<Store>) => string | number | undefined;
		}
	>,
	void
> = createHook(store, {
	selector: (state) => state.editorContext,
});
export const useChatContextWhiteboardContext: HookFunction<
	WhiteboardContextPayloadData,
	BoundActions<
		Store,
		{
			getAllContext: () => ({ getState }: StoreActionApi<Store>) => Readonly<Store>;
			getAIFeatureContext: () => ({
				getState,
			}: StoreActionApi<Store>) => ChatContextState | undefined;
			setAIFeatureContext: (
				chatContextData: ChatContextPayload,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setAIFeatureContextDismissed: (
				isDismissed: boolean,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDashboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData;
			setDashboardContext: (
				dashboardContext: DashboardInsightsActionsPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraWorkflowContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined;
			setJiraWorkflowContext: (
				jiraWorkflowContext: WorkflowContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraCreateContext: () => ({
				getState,
			}: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined;
			setJiraCreateContext: (
				jiraCreateContext: JiraCreateContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getEditorContext: () => ({ getState }: StoreActionApi<Store>) => EditorContextPayloadData;
			setEditorContext: (
				editorContext: EditorContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getWhiteboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WhiteboardContextPayloadData;
			setWhiteboardContext: (
				whiteboardContext: WhiteboardContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getSlidesContext: () => ({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData;
			setSlidesContext: (
				slidesContext: SlidesContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDatabaseContext: () => ({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData;
			setDatabaseContext: (
				databaseContext: DatabaseContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			mergeDatabaseContext: (
				partial: Partial<NonNullable<DatabaseContextPayloadData>>,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			getBrowserContext: () => ({
				getState,
			}: StoreActionApi<Store>) => BrowserContextPayloadData | undefined;
			setBrowserContext: (
				browserContext: BrowserContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			setPageSmartLinks: (
				pageSmartLinks: PageSmartLink[] | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getTarget: (
				contentContext: ContentContext,
				activeTargetId?: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentTarget | undefined;
			getContentContext: (
				contentId: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentContext | undefined;
			getActiveContext: () => ({ getState }: StoreActionApi<Store>) => ContentContext;
			hasTarget: (
				contentContext: ContentContext,
			) => ({ getState }: StoreActionApi<Store>) => boolean;
			setTarget: (
				toAdd: ContentContext,
				target: ContentTarget,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			clearTarget: (
				toRemove: ContentContext,
				targetId?: string | number,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setCurrentTargetId: (
				targetId: string | number | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getCurrentTargetId: () => ({
				getState,
			}: StoreActionApi<Store>) => string | number | undefined;
		}
	>,
	void
> = createHook(store, {
	selector: (state) => state.whiteboardContext,
});
export const useChatContextSlidesContext: HookFunction<
	SlidesContextPayloadData,
	BoundActions<
		Store,
		{
			getAllContext: () => ({ getState }: StoreActionApi<Store>) => Readonly<Store>;
			getAIFeatureContext: () => ({
				getState,
			}: StoreActionApi<Store>) => ChatContextState | undefined;
			setAIFeatureContext: (
				chatContextData: ChatContextPayload,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setAIFeatureContextDismissed: (
				isDismissed: boolean,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDashboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData;
			setDashboardContext: (
				dashboardContext: DashboardInsightsActionsPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraWorkflowContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined;
			setJiraWorkflowContext: (
				jiraWorkflowContext: WorkflowContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraCreateContext: () => ({
				getState,
			}: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined;
			setJiraCreateContext: (
				jiraCreateContext: JiraCreateContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getEditorContext: () => ({ getState }: StoreActionApi<Store>) => EditorContextPayloadData;
			setEditorContext: (
				editorContext: EditorContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getWhiteboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WhiteboardContextPayloadData;
			setWhiteboardContext: (
				whiteboardContext: WhiteboardContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getSlidesContext: () => ({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData;
			setSlidesContext: (
				slidesContext: SlidesContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDatabaseContext: () => ({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData;
			setDatabaseContext: (
				databaseContext: DatabaseContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			mergeDatabaseContext: (
				partial: Partial<NonNullable<DatabaseContextPayloadData>>,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			getBrowserContext: () => ({
				getState,
			}: StoreActionApi<Store>) => BrowserContextPayloadData | undefined;
			setBrowserContext: (
				browserContext: BrowserContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			setPageSmartLinks: (
				pageSmartLinks: PageSmartLink[] | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getTarget: (
				contentContext: ContentContext,
				activeTargetId?: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentTarget | undefined;
			getContentContext: (
				contentId: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentContext | undefined;
			getActiveContext: () => ({ getState }: StoreActionApi<Store>) => ContentContext;
			hasTarget: (
				contentContext: ContentContext,
			) => ({ getState }: StoreActionApi<Store>) => boolean;
			setTarget: (
				toAdd: ContentContext,
				target: ContentTarget,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			clearTarget: (
				toRemove: ContentContext,
				targetId?: string | number,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setCurrentTargetId: (
				targetId: string | number | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getCurrentTargetId: () => ({
				getState,
			}: StoreActionApi<Store>) => string | number | undefined;
		}
	>,
	void
> = createHook(store, {
	selector: (state) => state.slidesContext,
});
export const useChatContextDatabaseContext: HookFunction<
	DatabaseContextPayloadData,
	BoundActions<
		Store,
		{
			getAllContext: () => ({ getState }: StoreActionApi<Store>) => Readonly<Store>;
			getAIFeatureContext: () => ({
				getState,
			}: StoreActionApi<Store>) => ChatContextState | undefined;
			setAIFeatureContext: (
				chatContextData: ChatContextPayload,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setAIFeatureContextDismissed: (
				isDismissed: boolean,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDashboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData;
			setDashboardContext: (
				dashboardContext: DashboardInsightsActionsPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraWorkflowContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined;
			setJiraWorkflowContext: (
				jiraWorkflowContext: WorkflowContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraCreateContext: () => ({
				getState,
			}: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined;
			setJiraCreateContext: (
				jiraCreateContext: JiraCreateContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getEditorContext: () => ({ getState }: StoreActionApi<Store>) => EditorContextPayloadData;
			setEditorContext: (
				editorContext: EditorContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getWhiteboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WhiteboardContextPayloadData;
			setWhiteboardContext: (
				whiteboardContext: WhiteboardContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getSlidesContext: () => ({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData;
			setSlidesContext: (
				slidesContext: SlidesContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDatabaseContext: () => ({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData;
			setDatabaseContext: (
				databaseContext: DatabaseContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			mergeDatabaseContext: (
				partial: Partial<NonNullable<DatabaseContextPayloadData>>,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			getBrowserContext: () => ({
				getState,
			}: StoreActionApi<Store>) => BrowserContextPayloadData | undefined;
			setBrowserContext: (
				browserContext: BrowserContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			setPageSmartLinks: (
				pageSmartLinks: PageSmartLink[] | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getTarget: (
				contentContext: ContentContext,
				activeTargetId?: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentTarget | undefined;
			getContentContext: (
				contentId: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentContext | undefined;
			getActiveContext: () => ({ getState }: StoreActionApi<Store>) => ContentContext;
			hasTarget: (
				contentContext: ContentContext,
			) => ({ getState }: StoreActionApi<Store>) => boolean;
			setTarget: (
				toAdd: ContentContext,
				target: ContentTarget,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			clearTarget: (
				toRemove: ContentContext,
				targetId?: string | number,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setCurrentTargetId: (
				targetId: string | number | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getCurrentTargetId: () => ({
				getState,
			}: StoreActionApi<Store>) => string | number | undefined;
		}
	>,
	void
> = createHook(store, {
	selector: (state) => state.databaseContext,
});
export const useChatContextBrowserContext: HookFunction<
	BrowserContextPayloadData | undefined,
	BoundActions<
		Store,
		{
			getAllContext: () => ({ getState }: StoreActionApi<Store>) => Readonly<Store>;
			getAIFeatureContext: () => ({
				getState,
			}: StoreActionApi<Store>) => ChatContextState | undefined;
			setAIFeatureContext: (
				chatContextData: ChatContextPayload,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setAIFeatureContextDismissed: (
				isDismissed: boolean,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDashboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData;
			setDashboardContext: (
				dashboardContext: DashboardInsightsActionsPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraWorkflowContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined;
			setJiraWorkflowContext: (
				jiraWorkflowContext: WorkflowContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraCreateContext: () => ({
				getState,
			}: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined;
			setJiraCreateContext: (
				jiraCreateContext: JiraCreateContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getEditorContext: () => ({ getState }: StoreActionApi<Store>) => EditorContextPayloadData;
			setEditorContext: (
				editorContext: EditorContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getWhiteboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WhiteboardContextPayloadData;
			setWhiteboardContext: (
				whiteboardContext: WhiteboardContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getSlidesContext: () => ({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData;
			setSlidesContext: (
				slidesContext: SlidesContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDatabaseContext: () => ({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData;
			setDatabaseContext: (
				databaseContext: DatabaseContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			mergeDatabaseContext: (
				partial: Partial<NonNullable<DatabaseContextPayloadData>>,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			getBrowserContext: () => ({
				getState,
			}: StoreActionApi<Store>) => BrowserContextPayloadData | undefined;
			setBrowserContext: (
				browserContext: BrowserContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			setPageSmartLinks: (
				pageSmartLinks: PageSmartLink[] | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getTarget: (
				contentContext: ContentContext,
				activeTargetId?: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentTarget | undefined;
			getContentContext: (
				contentId: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentContext | undefined;
			getActiveContext: () => ({ getState }: StoreActionApi<Store>) => ContentContext;
			hasTarget: (
				contentContext: ContentContext,
			) => ({ getState }: StoreActionApi<Store>) => boolean;
			setTarget: (
				toAdd: ContentContext,
				target: ContentTarget,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			clearTarget: (
				toRemove: ContentContext,
				targetId?: string | number,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setCurrentTargetId: (
				targetId: string | number | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getCurrentTargetId: () => ({
				getState,
			}: StoreActionApi<Store>) => string | number | undefined;
		}
	>,
	void
> = createHook(store, {
	selector: (state) => state.browserContext,
});

export const useChatContextPageSmartLinks: HookFunction<
	| {
			orsAuthKey: string;
			generatorName?: string;
			count: number;
	  }[]
	| undefined,
	BoundActions<
		Store,
		{
			getAllContext: () => ({ getState }: StoreActionApi<Store>) => Readonly<Store>;
			getAIFeatureContext: () => ({
				getState,
			}: StoreActionApi<Store>) => ChatContextState | undefined;
			setAIFeatureContext: (
				chatContextData: ChatContextPayload,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setAIFeatureContextDismissed: (
				isDismissed: boolean,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDashboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData;
			setDashboardContext: (
				dashboardContext: DashboardInsightsActionsPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraWorkflowContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined;
			setJiraWorkflowContext: (
				jiraWorkflowContext: WorkflowContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraCreateContext: () => ({
				getState,
			}: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined;
			setJiraCreateContext: (
				jiraCreateContext: JiraCreateContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getEditorContext: () => ({ getState }: StoreActionApi<Store>) => EditorContextPayloadData;
			setEditorContext: (
				editorContext: EditorContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getWhiteboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WhiteboardContextPayloadData;
			setWhiteboardContext: (
				whiteboardContext: WhiteboardContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getSlidesContext: () => ({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData;
			setSlidesContext: (
				slidesContext: SlidesContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDatabaseContext: () => ({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData;
			setDatabaseContext: (
				databaseContext: DatabaseContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			mergeDatabaseContext: (
				partial: Partial<NonNullable<DatabaseContextPayloadData>>,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			getBrowserContext: () => ({
				getState,
			}: StoreActionApi<Store>) => BrowserContextPayloadData | undefined;
			setBrowserContext: (
				browserContext: BrowserContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			setPageSmartLinks: (
				pageSmartLinks: PageSmartLink[] | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getTarget: (
				contentContext: ContentContext,
				activeTargetId?: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentTarget | undefined;
			getContentContext: (
				contentId: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentContext | undefined;
			getActiveContext: () => ({ getState }: StoreActionApi<Store>) => ContentContext;
			hasTarget: (
				contentContext: ContentContext,
			) => ({ getState }: StoreActionApi<Store>) => boolean;
			setTarget: (
				toAdd: ContentContext,
				target: ContentTarget,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			clearTarget: (
				toRemove: ContentContext,
				targetId?: string | number,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setCurrentTargetId: (
				targetId: string | number | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getCurrentTargetId: () => ({
				getState,
			}: StoreActionApi<Store>) => string | number | undefined;
		}
	>,
	void
> = createHook(store, {
	selector: (state) => state.pageSmartLinks,
});

export const useChatContextAIFeatureContext: HookFunction<
	ChatContextState,
	BoundActions<
		Store,
		{
			getAllContext: () => ({ getState }: StoreActionApi<Store>) => Readonly<Store>;
			getAIFeatureContext: () => ({
				getState,
			}: StoreActionApi<Store>) => ChatContextState | undefined;
			setAIFeatureContext: (
				chatContextData: ChatContextPayload,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setAIFeatureContextDismissed: (
				isDismissed: boolean,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDashboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => DashboardInsightsActionsPayloadData;
			setDashboardContext: (
				dashboardContext: DashboardInsightsActionsPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraWorkflowContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WorkflowContextPayloadData | undefined;
			setJiraWorkflowContext: (
				jiraWorkflowContext: WorkflowContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getJiraCreateContext: () => ({
				getState,
			}: StoreActionApi<Store>) => JiraCreateContextPayloadData | undefined;
			setJiraCreateContext: (
				jiraCreateContext: JiraCreateContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getEditorContext: () => ({ getState }: StoreActionApi<Store>) => EditorContextPayloadData;
			setEditorContext: (
				editorContext: EditorContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getWhiteboardContext: () => ({
				getState,
			}: StoreActionApi<Store>) => WhiteboardContextPayloadData;
			setWhiteboardContext: (
				whiteboardContext: WhiteboardContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getSlidesContext: () => ({ getState }: StoreActionApi<Store>) => SlidesContextPayloadData;
			setSlidesContext: (
				slidesContext: SlidesContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getDatabaseContext: () => ({ getState }: StoreActionApi<Store>) => DatabaseContextPayloadData;
			setDatabaseContext: (
				databaseContext: DatabaseContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			mergeDatabaseContext: (
				partial: Partial<NonNullable<DatabaseContextPayloadData>>,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			getBrowserContext: () => ({
				getState,
			}: StoreActionApi<Store>) => BrowserContextPayloadData | undefined;
			setBrowserContext: (
				browserContext: BrowserContextPayloadData,
			) => ({ setState }: StoreActionApi<Store>) => void;
			setPageSmartLinks: (
				pageSmartLinks: PageSmartLink[] | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getTarget: (
				contentContext: ContentContext,
				activeTargetId?: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentTarget | undefined;
			getContentContext: (
				contentId: string | number,
			) => ({ getState }: StoreActionApi<Store>) => ContentContext | undefined;
			getActiveContext: () => ({ getState }: StoreActionApi<Store>) => ContentContext;
			hasTarget: (
				contentContext: ContentContext,
			) => ({ getState }: StoreActionApi<Store>) => boolean;
			setTarget: (
				toAdd: ContentContext,
				target: ContentTarget,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			clearTarget: (
				toRemove: ContentContext,
				targetId?: string | number,
			) => ({ getState, setState }: StoreActionApi<Store>) => void;
			setCurrentTargetId: (
				targetId: string | number | undefined,
			) => ({ setState }: StoreActionApi<Store>) => void;
			getCurrentTargetId: () => ({
				getState,
			}: StoreActionApi<Store>) => string | number | undefined;
		}
	>,
	void
> = createHook(store, {
	selector: (state) => state.aiFeatureContext,
});

// For testing
export { store };

export const useChatContextProvider = ({
	requestBrowserContent,
	getContentBody,
	getStagingSpace,
	useCurrentNonEditingPageAsContext,
	requestJiraContext,
}: {
	requestBrowserContent: () => Promise<BrowserContextCommandResult | undefined>;
	getContentBody: (
		type: ContentType,
		id: string | number,
	) => Promise<ReadContentCommandResult | undefined>;
	getStagingSpace: (id: string) => Promise<ReadSpaceCommandResult | undefined>;
	useCurrentNonEditingPageAsContext?: boolean;
	requestJiraContext?: () => Promise<JiraContextCommandResult | undefined>;
}): {
	getEditorContext: ({
		source,
		contentContext,
		isFirstPrompt,
		addCurrentPageAsContextUserPreference,
		activeTargetId,
	}: {
		source: string | undefined;
		contentContext: ContentContext;
		isFirstPrompt: boolean;
		addCurrentPageAsContextUserPreference: boolean;
		activeTargetId?: string | number;
	}) => Promise<EditorContextPayloadData>;
	getWhiteboardContext: ({
		source,
		contentContext,
		isFirstPrompt,
		addCurrentPageAsContextUserPreference,
		activeTargetId,
	}: {
		source: string | undefined;
		contentContext: ContentContext;
		isFirstPrompt: boolean;
		addCurrentPageAsContextUserPreference: boolean;
		activeTargetId?: string | number;
	}) => Promise<WhiteboardContextPayloadData>;
	getSlidesContext: ({
		source,
		contentContext,
		isFirstPrompt,
		addCurrentPageAsContextUserPreference,
		activeTargetId,
	}: {
		source: string | undefined;
		contentContext: ContentContext;
		isFirstPrompt: boolean;
		addCurrentPageAsContextUserPreference: boolean;
		activeTargetId?: string | number;
	}) => Promise<SlidesContextPayloadData>;
	getSpaceCreateContext: ({
		contentContext,
		isFirstPrompt,
	}: {
		contentContext: ContentContext;
		isFirstPrompt: boolean;
	}) => Promise<ReadSpaceCommandResult | undefined> | undefined;
	getIsStagingArea: () => boolean;
	useCurrentBrowsingContext: (
		addCurrentPageAsContextUserPreference: boolean,
	) => boolean | undefined;
	getDatabaseContext: ({
		source,
		contentContext,
		isFirstPrompt,
		addCurrentPageAsContextUserPreference,
		activeTargetId,
	}: {
		source: string | undefined;
		contentContext: ContentContext;
		isFirstPrompt: boolean;
		addCurrentPageAsContextUserPreference: boolean;
		activeTargetId?: string | number;
	}) => Promise<DatabaseContextPayloadData>;
	getDashboardEditorContext: ({
		contentContext,
		activeTargetId,
	}: {
		contentContext: ContentContext;
		activeTargetId?: string | number;
	}) => Promise<DashboardInsightsActionsPayloadData>;
	getBrowserContext: ({
		addCurrentPageAsContextUserPreference,
		enabled,
	}: {
		addCurrentPageAsContextUserPreference: boolean;
		enabled: boolean;
	}) => Promise<BrowserContextPayloadData | undefined>;
	getJiraContext: () => Promise<JiraCreateContextPayloadData | JiraContextData | undefined>;
	getAllContext: () => Readonly<Store>;
	getAIFeatureContext: () => ChatContextState | undefined;
	setAIFeatureContext: (chatContextData: ChatContextPayload) => void;
	setAIFeatureContextDismissed: (isDismissed: boolean) => void;
	getDashboardContext: () => DashboardInsightsActionsPayloadData;
	setDashboardContext: (dashboardContext: DashboardInsightsActionsPayloadData) => void;
	getJiraWorkflowContext: () => WorkflowContextPayloadData | undefined;
	setJiraWorkflowContext: (jiraWorkflowContext: WorkflowContextPayloadData) => void;
	getJiraCreateContext: () => JiraCreateContextPayloadData | undefined;
	setJiraCreateContext: (jiraCreateContext: JiraCreateContextPayloadData) => void;
	setEditorContext: (editorContext: EditorContextPayloadData) => void;
	setWhiteboardContext: (whiteboardContext: WhiteboardContextPayloadData) => void;
	setSlidesContext: (slidesContext: SlidesContextPayloadData) => void;
	setDatabaseContext: (databaseContext: DatabaseContextPayloadData) => void;
	mergeDatabaseContext: (
		partial: Partial<{
			contentId: string;
			csv: string;
			title: string;
			url: string;
			selectedElementIds?: string[];
		}>,
	) => void;
	setBrowserContext: (browserContext: BrowserContextPayloadData) => void;
	setPageSmartLinks: (
		pageSmartLinks:
			| {
					orsAuthKey: string;
					generatorName?: string;
					count: number;
			  }[]
			| undefined,
	) => void;
	getTarget: (
		contentContext: ContentContext,
		activeTargetId?: string | number | undefined,
	) => ContentTarget | undefined;
	getContentContext: (contentId: string | number) => ContentContext | undefined;
	getActiveContext: () => ContentContext;
	hasTarget: (contentContext: ContentContext) => boolean;
	setTarget: (toAdd: ContentContext, target: ContentTarget) => void;
	clearTarget: (toRemove: ContentContext, targetId?: string | number | undefined) => void;
	setCurrentTargetId: (targetId: string | number | undefined) => void;
	getCurrentTargetId: () => string | number | undefined;
} => {
	const actions = useChatContextStoreActions();
	const { getTarget, getCurrentTargetId } = actions;

	const fetchContent = useCallback(
		async (type: ContentType, contentContext: ContentContext, activeTargetId?: string | number) => {
			const currentTargetId = getCurrentTargetId();
			const contentTarget = getTarget(contentContext, activeTargetId);

			if (!contentTarget) {
				return undefined;
			}

			// Only use currentTargetId when staging-area doesn't exist
			// to avoid changing any content open behind the staging
			// area modal
			const shouldUseCurrentTargetId = type === 'page' && !getTarget('staging-area');

			const selectedTargetId = shouldUseCurrentTargetId
				? currentTargetId || contentTarget.id
				: contentTarget.id;

			const contentBody: ReadContentCommandResult | undefined = await getContentBody(
				type,
				selectedTargetId,
			);
			if (!contentBody || contentBody.contentType !== type) {
				return undefined;
			}

			switch (contentBody.contentType) {
				case 'page': {
					if (!contentBody.pageBody) {
						return undefined;
					}

					return {
						contentId: contentBody.contentId,
						editorStreamingContractVersion: contentBody.editorStreamingContractVersion,
						document: {
							type: 'text/adf' as const,
							content: contentBody.pageBody,
						},
						selection: {
							type: 'text/markdown' as const,
							content: contentBody.selectionMarkdown || '',
						},
						selectionFragment: contentBody.selectionFragment,
						selectionLocalIds: contentBody.selectionLocalIds,
						contentMauiId: contentBody.contentMauiId,
						dynamicUiSource: contentBody.dynamicUiSource,
						dynamicUiType: contentBody.dynamicUiType,
						mediaFileId: contentBody.mediaFileId,
						isViewMode: contentBody.isViewMode,
						isDraftLockedForEditing: contentBody.isDraftLockedForEditing,
						useGenericEditorSkill: contentBody.useGenericEditorSkill,
						additionalContext: contentBody.additionalContext,
					};
				}
				case 'whiteboard': {
					if (!contentBody.svg || !contentBody.wdf) {
						return undefined;
					}
					return {
						contentId: contentBody.contentId,
						type: 'image/svg+xml' as const,
						svg: contentBody.svg,
						title: contentBody.title ?? null,
						url: contentBody.url ?? null,
						wdf: contentBody.wdf,
						selectedElementIds: contentBody.selectedElementIds,
						isViewMode: contentBody.isViewMode,
					};
				}
				case 'slide': {
					if (!contentBody.xml) {
						return undefined;
					}
					return {
						contentId: contentBody.contentId,
						xml: contentBody.xml,
						title: contentBody.title,
						url: contentBody.url,
						selectedSlideIndex: contentBody.selectedSlideIndex,
						selectedElementIds: contentBody.selectedElementIds,
						isViewMode: contentBody.isViewMode,
					};
				}
				case 'database': {
					if (!contentBody.csv) {
						return undefined;
					}

					return {
						contentId: contentBody.contentId,
						csv: contentBody.csv,
						title: contentBody.title,
						url: contentBody.url,
						isViewMode: contentBody.isViewMode,
					};
				}
				case 'dashboard': {
					if (!contentBody.content) {
						return undefined;
					}

					return {
						content: contentBody.content,
						dashboardAri: contentBody.dashboardAri,
						isEditing: contentBody.isEditing,
						chartResponseData: contentBody.chartResponseData,
					};
				}
				default:
					return undefined;
			}
		},
		[getContentBody, getTarget, getCurrentTargetId],
	);

	/**
	 * Fetches the context of a currently live page with Rovo chat open.
	 *
	 * @description see full list of cases to consider in {@link getContextRequirements}
	 */
	const getEditorContext = useCallback(
		async ({
			source,
			contentContext,
			isFirstPrompt,
			addCurrentPageAsContextUserPreference,
			activeTargetId,
		}: {
			source: string | undefined;
			contentContext: ContentContext;
			isFirstPrompt: boolean;
			addCurrentPageAsContextUserPreference: boolean;
			activeTargetId?: string | number;
		}): Promise<EditorContextPayloadData> => {
			const isAifcEnabled = fg('aifc_create_enabled');
			const shouldProvideContext = getContextRequirements({
				contentContext,
				source,
				isFirstPrompt,
				isAifcEnabled,
				userPreference: addCurrentPageAsContextUserPreference,
			});
			if (!shouldProvideContext) {
				return undefined;
			}

			return isAifcEnabled
				? ((await fetchContent('page', contentContext, activeTargetId)) as EditorContextPayloadData)
				: actions.getEditorContext();
		},
		[actions, fetchContent],
	);

	/**
	 * Fetches the context of a currently live page with Rovo chat open.
	 *
	 * @description see full list of cases to consider in {@link getContextRequirements}
	 */
	const getWhiteboardContext = useCallback(
		async ({
			source,
			contentContext,
			isFirstPrompt,
			addCurrentPageAsContextUserPreference,
			activeTargetId,
		}: {
			source: string | undefined;
			contentContext: ContentContext;
			isFirstPrompt: boolean;
			addCurrentPageAsContextUserPreference: boolean;
			activeTargetId?: string | number;
		}): Promise<WhiteboardContextPayloadData> => {
			const isAifcEnabled = fg('aifc_create_enabled');
			const shouldProvideContext = getContextRequirements({
				contentContext,
				source,
				isFirstPrompt,
				isAifcEnabled,
				userPreference: addCurrentPageAsContextUserPreference,
			});
			if (!shouldProvideContext) {
				return undefined;
			}

			return isAifcEnabled
				? ((await fetchContent(
						'whiteboard',
						contentContext,
						activeTargetId,
					)) as WhiteboardContextPayloadData)
				: actions.getWhiteboardContext();
		},
		[actions, fetchContent],
	);

	const getSlidesContext = useCallback(
		async ({
			source,
			contentContext,
			isFirstPrompt,
			addCurrentPageAsContextUserPreference,
			activeTargetId,
		}: {
			source: string | undefined;
			contentContext: ContentContext;
			isFirstPrompt: boolean;
			addCurrentPageAsContextUserPreference: boolean;
			activeTargetId?: string | number;
		}): Promise<SlidesContextPayloadData> => {
			const isAifcEnabled = FeatureGates.getExperimentValue<boolean>(
				'cc-mui-slides-experiment',
				'isEnabled',
				false,
			);
			const shouldProvideContext = getContextRequirements({
				contentContext,
				source,
				isFirstPrompt,
				isAifcEnabled,
				userPreference: addCurrentPageAsContextUserPreference,
			});
			if (!shouldProvideContext) {
				return undefined;
			}

			return isAifcEnabled
				? ((await fetchContent(
						'slide',
						contentContext,
						activeTargetId,
					)) as SlidesContextPayloadData)
				: actions.getSlidesContext();
		},
		[actions, fetchContent],
	);

	const getDatabaseContext = useCallback(
		async ({
			source,
			contentContext,
			isFirstPrompt,
			addCurrentPageAsContextUserPreference,
			activeTargetId,
		}: {
			source: string | undefined;
			contentContext: ContentContext;
			isFirstPrompt: boolean;
			addCurrentPageAsContextUserPreference: boolean;
			activeTargetId?: string | number;
		}): Promise<DatabaseContextPayloadData> => {
			const isAifcEnabled = fg('aifc_create_enabled');
			const shouldProvideContext = getContextRequirements({
				contentContext,
				source,
				isFirstPrompt,
				isAifcEnabled,
				userPreference: addCurrentPageAsContextUserPreference,
			});
			if (!shouldProvideContext) {
				return undefined;
			}

			return isAifcEnabled
				? ((await fetchContent(
						'database',
						contentContext,
						activeTargetId,
					)) as DatabaseContextPayloadData)
				: actions.getDatabaseContext();
		},
		[actions, fetchContent],
	);

	const getDashboardEditorContext = useCallback(
		async ({
			contentContext,
			activeTargetId,
		}: {
			contentContext: ContentContext;
			activeTargetId?: string | number;
		}): Promise<DashboardInsightsActionsPayloadData> => {
			if (!fg('platform_avp_aifc_enabled')) {
				return undefined;
			}

			return (await fetchContent(
				'dashboard',
				contentContext,
				activeTargetId,
			)) as DashboardInsightsActionsPayloadData;
		},
		[fetchContent],
	);

	const getSpaceCreateContext = useCallback(
		({
			contentContext,
			isFirstPrompt,
		}: {
			contentContext: ContentContext;
			isFirstPrompt: boolean;
		}): Promise<ReadSpaceCommandResult | undefined> | undefined => {
			if (
				contentContext === 'staging-area' &&
				!isFirstPrompt &&
				fg('aifc_space_creation_enabled')
			) {
				return getStagingSpace(String(getTarget(contentContext)?.id));
			}
		},
		[getStagingSpace, getTarget],
	);

	const getIsStagingArea = useCallback(
		(): boolean => actions.getActiveContext() === 'staging-area',
		[actions],
	);

	const useCurrentBrowsingContext = useCallback(
		(addCurrentPageAsContextUserPreference: boolean): boolean | undefined => {
			// If the user has explicitly turned off "use current page as context" (e.g.
			// by dismissing the context banner), send false rather than undefined so the
			// backend is explicitly told not to use the browserUrl as context. This is
			// required in edit mode, where useCurrentNonEditingPageAsContext is undefined
			// but the user preference is set to false on dismiss. the value is sent as true
			// when the context is enabled in the chat banner.
			if (addCurrentPageAsContextUserPreference && fg('rovo_context_flag')) {
				return addCurrentPageAsContextUserPreference;
			}
			// undefined means don't send useCurrentBrowsingContext at all. false tells backend to explicitly not use the browserUrl as context.
			if (useCurrentNonEditingPageAsContext === undefined) {
				return undefined;
			}
			const stagingAreaEnabled = getIsStagingArea();

			return (
				!stagingAreaEnabled &&
				addCurrentPageAsContextUserPreference &&
				useCurrentNonEditingPageAsContext
			);
		},
		[useCurrentNonEditingPageAsContext, getIsStagingArea],
	);

	const getBrowserContext = useCallback(
		async ({
			addCurrentPageAsContextUserPreference,
			enabled,
		}: {
			addCurrentPageAsContextUserPreference: boolean;
			enabled: boolean;
		}): Promise<BrowserContextPayloadData | undefined> => {
			// if the feature is not enabled, we return the original context resolution.
			if (
				!enabled &&
				!FeatureGates.getExperimentValue('jira_free_gen_editor', 'isEnabled', false)
			) {
				return actions.getBrowserContext();
			}

			if (!addCurrentPageAsContextUserPreference) {
				return undefined;
			}

			// Request browser content from the extension
			const browserContextResult = await requestBrowserContent();
			const browserContext = browserContextResult
				? { context: browserContextResult }
				: actions.getBrowserContext();

			return browserContext;
		},
		[requestBrowserContent, actions],
	);

	const getJiraContext = useCallback(async (): Promise<
		JiraCreateContextPayloadData | JiraContextData | undefined
	> => {
		if (fg('jira-aifc-enable-preview-card-in-chat-v2')) {
			const isStagingArea = getIsStagingArea();
			if (!isStagingArea) {
				const jiraCreateContext = actions.getJiraCreateContext();
				if (jiraCreateContext?.draftWorkItems) {
					return jiraCreateContext;
				}
			}
		}

		// Fallback to bridge API if available (staging area doesn't have the state in sync with the chat store)
		if (!requestJiraContext) {
			return undefined;
		}

		try {
			const result = await requestJiraContext();
			return result?.success ? result.context : undefined;
			// eslint-disable-next-line no-unused-vars
		} catch (error) {
			return undefined;
		}
	}, [requestJiraContext, actions, getIsStagingArea]);

	return {
		...actions,
		getEditorContext,
		getWhiteboardContext,
		getSlidesContext,
		getSpaceCreateContext,
		getIsStagingArea,
		useCurrentBrowsingContext,
		getDatabaseContext,
		getDashboardEditorContext,
		getBrowserContext,
		getJiraContext,
	};
};

type ContextRequirementOpts = {
	/**
	 * Current rendering context of Rovo chat
	 */
	contentContext: ContentContext;
	/**
	 * The indicated source of the interaction
	 */
	source: string | undefined;
	/**
	 * Whether this is the first prompt / message in a new conversation
	 */
	isFirstPrompt: boolean;
	/**
	 * Whether the user has toggled ON that they wish to use the context
	 * of the current content they are on
	 * @description NOTE: This is not respected verbatim. See {@link getContextRequirements}.
	 */
	userPreference: boolean;
	/**
	 * Whether AI first creation is enabled.
	 */
	isAifcEnabled: boolean;
};

/**
 * Determines if the context of a currently live piece of content should be yielded.
 *
 * @description
 * There are three cases to consider here:
 *
 * 1. Staging area context
 * 2. Inline editor
 * 3. Global context
 *
 * Our general heuristic is: we need to respect the user's preference if we are on real content, unless it
 * is an inline editing interaction. In the staging area, we similarly give inline editing experiences the
 * context at all times, but otherwise do NOT want to if it is a fresh conversation.
 *
 * Context is important for updates. Key aspect to consider: When does a user want to update their content?
 * When an inline edit is happening, they have explicitly selected content already, and hence want an edit.
 * If they are in the staging area and it is not the first prompt, they want an edit. Hence the below.
 */
export function getContextRequirements({
	contentContext,
	source,
	isFirstPrompt,
	isAifcEnabled,
	userPreference,
}: ContextRequirementOpts): boolean {
	/**
	 * If AIFC is not enabled, we default to the user's preference.
	 */
	if (!isAifcEnabled) {
		return userPreference;
	}

	/**
	 * In the staging area, we always want to provide context if the source is the
	 * inline editor, from the blank object experience, or if the prompt is not the
	 * first in a new conversation.
	 */

	const sourceEligible =
		source === 'CONFLUENCE_INLINE_EDITOR' ||
		source === 'INLINE_EDITOR' ||
		source === 'CONFLUENCE_MAUI_INLINE_EDITOR' ||
		source === 'CONFLUENCE_WHITEBOARD_TO_PAGE_BUTTON' ||
		source === 'CONFLUENCE_WHITEBOARD_BRAINSTORMING_FOLLOW_UP' ||
		source === 'CONFLUENCE_CWR_TEMPLATE' ||
		source === 'CONFLUENCE_CWR_BLANK_OBJECT';
	if (contentContext === 'staging-area') {
		return sourceEligible || !isFirstPrompt;
	} else {
		/**
		 * On real content when AIFC is enabled, we only want to default to true
		 * if the source of the content is the inline editing experience. Otherwise,
		 * we want to respect the preference chosen by the user.
		 */
		return sourceEligible || userPreference;
	}
}
